import { useCallback } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSmartLinkContext } from '@atlaskit/link-provider';

import type { FlagsStateContainer } from '@confluence/flags';
import { fg } from '@confluence/feature-gating';

import type { EligibilityCheckResult } from '../data-types';
import { isExperimentEnabled } from '../services/feature-flag';
import { getJiraSmartLinks } from '../services/links';
import { isUserOptOut } from '../services/opt-out';
import { useFeatureExposed } from '../services/feature-exposure';
import { useGetInviteesFromExpApi } from '../services/get-invitees/get-invitees-from-expApi';

import { useInvitePromptFlag } from './useInvitePromptFlag';

export const useInviteOnLinkInsertEligibilityCheck = (flags?: FlagsStateContainer) => {
	const { store } = useSmartLinkContext();
	const getInvitees = useGetInviteesFromExpApi();
	const fireExposure = useFeatureExposed();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async (
			pageId: string,
			content: ADFEntity,
			pageVersion: number,
			startingLinks?: Set<string>,
		): Promise<EligibilityCheckResult | undefined> => {
			if (
				startingLinks === undefined ||
				flags === undefined ||
				!fg('invite_on_link_insert_confluence_gate')
			) {
				return;
			}

			try {
				const newLinkData = getJiraSmartLinks(content, store);
				for (const [link, data] of Object.entries(newLinkData)) {
					if (!startingLinks?.has(link)) {
						const { tenantId: jiraCloudId, name } = data;
						const invitees = await getInvitees(pageId, jiraCloudId);

						if (invitees.length > 0) {
							fireExposure('ELIGIBLE');
							if (!isExperimentEnabled() || isUserOptOut()) return;
							return { invitees, targetTitle: name, continueUrl: link, jiraCloudId, pageVersion };
						}
						break;
					}
				}
			} catch (error) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'checkEligibility',
						source: 'inviteOnLinkInsert',
						attributes: {
							message: String(error),
							details: { ...(error || {}) },
						},
					},
				}).fire();
			}
		},
		[createAnalyticsEvent, fireExposure, flags, getInvitees, store],
	);
};

export const useShowInviteOnLinkInsertFlag = (flags?: FlagsStateContainer) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showInviteFlag = useInvitePromptFlag(flags);
	return useCallback(
		async ({
			invitees,
			targetTitle,
			continueUrl,
			jiraCloudId,
			pageVersion,
		}: EligibilityCheckResult) => {
			try {
				if (!fg('invite_on_link_insert_confluence_gate')) return;
				showInviteFlag(invitees, targetTitle, continueUrl, jiraCloudId, pageVersion);
			} catch (error) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'showInviteFlag',
						source: 'inviteOnLinkInsert',
						attributes: {
							message: String(error),
							details: { ...(error || {}) },
						},
					},
				}).fire();
			}
		},
		[createAnalyticsEvent, showInviteFlag],
	);
};
